import type { ProjectDetail } from "@esi/shared";
import {
  BookOpenCheck,
  Brain,
  CircleDot,
  FileWarning,
  KeyRound,
  TriangleAlert,
  Undo2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatAgo } from "../../i18n/format.ts";
import { useProjectMemories } from "../../memory/queries.ts";
import {
  changeSetPath,
  Link,
  projectHomePath,
  projectSettingsPath,
  projectTaskPath,
  wikiChangesPath,
} from "../../router.tsx";
import { calendarDate } from "../../tasks/parts.tsx";
import { denied, useTaskPages } from "../../tasks/queries.ts";
import { useChangesInbox } from "../../wiki/queries.ts";
import { can } from "../capabilities.ts";
import { useAccessRequests } from "../settings/AccessRequestsSection.tsx";
import { HomeEmptyLine, HomeRow, HomeRows, HomeSection, stateOf } from "./HomeSection.tsx";
import { NEEDS_YOU_ROWS, type NeedsYouRow, needsYouRows } from "./needs-you.ts";

/** The open tasks assigned to the viewer: the Tasks tab's own query for "assigned to me". */
export const MY_OPEN_TASKS = { status: "open", assignee: "me" } as const;

/**
 * Needs you (spec `project-home` "Needs you"): what waits for the viewer, at most 8 rows, the
 * total in the heading. A source the viewer may not read is never requested: review rows need
 * `wiki.view_drafts`, the memory row `memory.manage`, the access-requests row `members.manage`.
 */
export function NeedsYou({
  project,
  omitWhenEmpty = false,
}: {
  project: ProjectDetail;
  /** Beside Getting started: shown once something waits or a source failed (not while loading). */
  omitWhenEmpty?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const viewDrafts = can(project, "wiki.view_drafts");
  const manageMemory = can(project, "memory.manage");
  const { queue, inbox } = useChangesInbox(project, viewDrafts);
  const tasks = useTaskPages(project.id, { ...MY_OPEN_TASKS });
  const memories = useProjectMemories(project.id, "pending", "", { enabled: manageMemory });
  const requests = useAccessRequests(project);
  // Tasks off for the project, or none to see: no task rows, not an error.
  const tasksDenied = tasks.isError && denied(tasks.error);
  const manageMembers = can(project, "members.manage");
  const queries = [
    viewDrafts ? queue : null,
    tasksDenied ? null : tasks,
    manageMemory ? memories : null,
    manageMembers ? requests : null,
  ].filter((q) => q !== null);
  const state = stateOf(queries, 3);
  const memoryCount = manageMemory ? (memories.data?.pendingCount ?? 0) : 0;
  const rows = needsYouRows({
    review: viewDrafts ? inbox : null,
    tasks: tasksDenied ? [] : (tasks.data?.pages[0]?.tasks ?? []),
    memories:
      memoryCount > 0
        ? {
            count: memoryCount,
            // The queue comes oldest first: the row dates from its newest candidate.
            at: (memories.data?.items ?? []).reduce(
              (at, m) => (m.createdAt > at ? m.createdAt : at),
              "",
            ),
          }
        : null,
    // Gated like the query: a downgrade leaves the disabled query's cache behind.
    accessRequests:
      manageMembers && requests.data?.length
        ? {
            count: requests.data.length,
            at: requests.data.reduce((at, r) => (r.createdAt > at ? r.createdAt : at), ""),
          }
        : null,
  });
  // Omitted only once every source answered with nothing: an error keeps its Retry.
  if (omitWhenEmpty && !state.error && (state.pending || rows.length === 0)) {
    return null;
  }
  const shown = rows.slice(0, NEEDS_YOU_ROWS);
  const hidden = rows.slice(NEEDS_YOU_ROWS);
  const moreReview = hidden.some((r) => r.kind === "changeSet" || r.kind === "reviewItem");
  // More open tasks than the first page (50): the totals are lower bounds, said with a "+".
  const moreTaskPages = !tasksDenied && tasks.hasNextPage;
  const moreTasks = moreTaskPages || hidden.some((r) => r.kind === "task");
  const moreMemories = hidden.some((r) => r.kind === "memories");
  const moreRequests = hidden.some((r) => r.kind === "accessRequests");
  return (
    <HomeSection
      id="needs-you"
      title={t("projectOverview.needsYou.title")}
      {...(!state.pending && !state.error && rows.length > 0
        ? {
            count: moreTaskPages ? `${rows.length}+` : rows.length,
            countLabel: moreTaskPages
              ? t("projectOverview.needsYou.countAtLeast", { count: rows.length })
              : t("projectOverview.needsYou.count", { count: rows.length }),
          }
        : {})}
      state={state}
      foot={
        hidden.length > 0 || moreTaskPages ? (
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              {moreTaskPages
                ? t("projectOverview.needsYou.moreAtLeast", { count: hidden.length })
                : t("projectOverview.needsYou.more", { count: hidden.length })}
            </span>
            {moreReview ? (
              <Link
                href={wikiChangesPath(project.id)}
                className="text-accent-text underline-offset-4 hover:underline"
              >
                {t("projectOverview.needsYou.showReview")}
              </Link>
            ) : null}
            {moreTasks ? (
              <Link
                href={projectHomePath(project.id, "tasks")}
                className="text-accent-text underline-offset-4 hover:underline"
              >
                {t("projectOverview.needsYou.showTasks")}
              </Link>
            ) : null}
            {moreMemories ? (
              <Link
                href={projectHomePath(project.id, "memory")}
                className="text-accent-text underline-offset-4 hover:underline"
              >
                {t("projectOverview.needsYou.showMemories")}
              </Link>
            ) : null}
            {moreRequests ? (
              <Link
                href={projectSettingsPath(project.id, "members")}
                className="text-accent-text underline-offset-4 hover:underline"
              >
                {t("projectOverview.needsYou.showAccessRequests")}
              </Link>
            ) : null}
          </span>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <HomeEmptyLine>{t("projectOverview.needsYou.empty")}</HomeEmptyLine>
      ) : (
        <HomeRows>
          {shown.map((row) => (
            <NeedsYouItem
              key={`${row.kind}:${row.id}`}
              project={project}
              row={row}
              lang={i18n.language}
            />
          ))}
        </HomeRows>
      )}
    </HomeSection>
  );
}

function NeedsYouItem(props: { project: ProjectDetail; row: NeedsYouRow; lang: string }) {
  const { project, row } = props;
  const { t } = useTranslation();
  switch (row.kind) {
    case "changeSet": {
      const { set } = row;
      const pages = set.items.length;
      return row.sentBack ? (
        <HomeRow
          icon={Undo2}
          href={changeSetPath(project.id, set.id)}
          title={t("projectOverview.needsYou.revise", { title: set.title })}
          meta={[
            t("projectOverview.needsYou.sentBack"),
            set.reviewCommentByName
              ? t("projectOverview.by", { name: set.reviewCommentByName })
              : null,
          ]}
          at={row.at}
          atLabel={formatAgo(row.at)}
        />
      ) : (
        <HomeRow
          icon={BookOpenCheck}
          href={changeSetPath(project.id, set.id)}
          title={t("projectOverview.needsYou.review", { title: set.title })}
          meta={[
            t("projectOverview.pages", { count: pages }),
            set.provenance.createdByName ?? null,
          ]}
          at={row.at}
          atLabel={formatAgo(row.at)}
        />
      );
    }
    case "reviewItem":
      return (
        <HomeRow
          icon={FileWarning}
          href={wikiChangesPath(project.id)}
          title={
            row.item.page
              ? t(`projectOverview.needsYou.item.${row.item.kind}`, { title: row.item.page.title })
              : t(`projectOverview.needsYou.itemUntitled.${row.item.kind}`)
          }
          meta={[row.item.source ? row.item.source.title : null]}
          at={row.at}
          atLabel={formatAgo(row.at)}
        />
      );
    case "task": {
      const { task } = row;
      const due = task.dueDate ? calendarDate(task.dueDate, props.lang) : null;
      return (
        <HomeRow
          icon={row.overdue ? TriangleAlert : CircleDot}
          tone={row.overdue ? "warning" : undefined}
          href={projectTaskPath(project.id, task.id)}
          title={task.hidden ? t("projectOverview.needsYou.hiddenTask") : task.title}
          meta={[
            row.overdue ? (
              <span key="overdue" className="font-medium text-warning-fg">
                {t("tasks.overdue")}
              </span>
            ) : null,
            due ? t("projectOverview.needsYou.due", { date: due }) : t("tasks.noDue"),
            t(`tasks.${task.status}`),
          ]}
        />
      );
    }
    case "memories":
      return (
        <HomeRow
          icon={Brain}
          href={projectHomePath(project.id, "memory")}
          title={t("projectOverview.needsYou.memories", { count: row.count })}
          {...(row.at ? { at: row.at, atLabel: formatAgo(row.at) } : {})}
        />
      );
    case "accessRequests":
      return (
        <HomeRow
          icon={KeyRound}
          href={projectSettingsPath(project.id, "members")}
          title={t("projectOverview.needsYou.accessRequests", { count: row.count })}
          {...(row.at ? { at: row.at, atLabel: formatAgo(row.at) } : {})}
        />
      );
  }
}
